/**
 * ErrorBoundary — global xato tutuvchi
 *
 * Spec bo'lim 20: Qizil to'rtburchak, xato matni, qayta urinish tugmasi.
 * React class component (hooks ErrorBoundary qo'llab-quvvatlamaydi).
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const RELOAD_FLAG = 'avfx-eb-reload';

/**
 * Tiklab bo'lmaydigan xato — eski/mos kelmaydigan bundle (stale chunk) yoki
 * cheksiz rekursiya. Bunday holatda React tree'ni qayta render qilish FOYDASIZ
 * (yana darhol crash bo'ladi) — yagona yechim: sahifani to'liq qayta yuklash
 * (yangi bundle olinadi). Beqaror internetda yangi deploy + eski cache shu xatoni
 * berishi mumkin (2026-06-01 foydalanuvchi: "Maximum call stack").
 */
function isUnrecoverable(error: Error | null): boolean {
  const text = `${error?.name ?? ''} ${error?.message ?? ''}`;
  return /Maximum call stack|ChunkLoadError|dynamically imported module|Importing a module script failed|Failed to fetch dynamically|Unexpected token|Loading chunk|module script failed/i.test(
    text,
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
    // Stale-chunk / stack-overflow → BIR MARTA avtomatik hard reload (loop-guard:
    // 15s ichida takror reload qilmaymiz — agar reload'dan keyin ham crash bo'lsa,
    // xato UI'sini ko'rsatamiz).
    if (isUnrecoverable(error)) {
      try {
        const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
        if (Date.now() - last > 15_000) {
          sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
          window.location.reload();
        }
      } catch {
        /* sessionStorage yo'q — e'tiborsiz */
      }
    }
  }

  private readonly handleRetry = (): void => {
    // Tiklab bo'lmaydigan xato → to'liq qayta yuklash (yangi bundle).
    // Boshqa (vaqtinchalik) xatolar → React tree'ni qayta render.
    if (isUnrecoverable(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  private readonly handleHardReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-xl border border-destructive/40 bg-destructive/5 p-8 text-center">
        <AlertTriangle className="size-12 text-destructive" />
        <div className="space-y-2">
          <h2 className="text-lg font-bold text-destructive">Xatolik yuz berdi</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {this.state.error?.message ?? "Kutilmagan xatolik. Qayta urinib ko'ring."}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={this.handleRetry}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <RefreshCw className="size-4" />
            Qayta urinish
          </button>
          <button
            type="button"
            onClick={this.handleHardReload}
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          >
            Sahifani to&apos;liq yangilash
          </button>
        </div>
      </div>
    );
  }
}
